import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per squash match
 * {
 *   matchType: "recreational" | "competitive" | "tournament",
 *   bestOf: number, // 3 or 5
 *   pointsToWin: number, // 11 or 15
 *   playerA: string | null,
 *   playerAId: string | null,
 *   playerB: string | null,
 *   playerBId: string | null,
 *   games: [{ gameNumber, playerAPoints, playerBPoints, winner, duration }],
 *   playerAGames: number,
 *   playerBGames: number,
 *   winner: "playerA" | "playerB" | null,
 *   duration: number,
 *   courtNumber: number
 * }
 */

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

export const AddSquash = () => {
  const location = useLocation();
  const data = location.state || {};
  const navigate = useNavigate();

  const [squashMatches, setSquashMatches] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  // Tailwind class helpers for consistent styling
  const brandBlue = "#3498db";
  const brandBlueDark = "#2980b9";

  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noMatchesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
  };

  const validateMatches = () => {
    const errors = [];

    squashMatches.forEach((match, index) => {
      // Check if players are selected
      if (!match.playerA || !match.playerB) {
        errors.push(`Match ${index + 1}: Both players must be selected`);
      }

      // Check if same player is selected for both positions
      if (match.playerA && match.playerB && match.playerA === match.playerB) {
        errors.push(
          `Match ${index + 1}: Cannot select the same player for both positions`
        );
      }

      // Check if match has games
      if (!match.games || match.games.length === 0) {
        errors.push(`Match ${index + 1}: Match must have at least one game`);
      }

      // Validate games
      if (match.games && match.games.length > 0) {
        match.games.forEach((game, gameIndex) => {
          // Check for valid point scores (0-21, typically)
          if (game.playerAPoints > 21 || game.playerBPoints > 21) {
            errors.push(
              `Match ${index + 1}, Game ${
                gameIndex + 1
              }: Points cannot exceed 21`
            );
          }

          // Check for valid game completion (someone must reach the target)
          if (
            game.playerAPoints < match.pointsToWin &&
            game.playerBPoints < match.pointsToWin
          ) {
            errors.push(
              `Match ${index + 1}, Game ${
                gameIndex + 1
              }: At least one player must reach ${match.pointsToWin} points`
            );
          }

          // Check for proper winning margin (must win by 2 if score is tied near target)
          const targetPoints = match.pointsToWin;
          if (
            game.playerAPoints >= targetPoints - 1 &&
            game.playerBPoints >= targetPoints - 1
          ) {
            const diff = Math.abs(game.playerAPoints - game.playerBPoints);
            if (
              diff < 2 &&
              (game.playerAPoints < targetPoints + 10 ||
                game.playerBPoints < targetPoints + 10)
            ) {
              if (
                Math.max(game.playerAPoints, game.playerBPoints) <
                  targetPoints ||
                diff < 2
              ) {
                errors.push(
                  `Match ${index + 1}, Game ${
                    gameIndex + 1
                  }: Must win by 2 points when score is close`
                );
              }
            }
          }
        });
      }

      // Check if match has any games
      if (match.games.length === 0) {
        errors.push(
          `Match ${index + 1}: Match must have at least one game added`
        );
      }

      // Check if match has a winner
      if (match.playerAGames === 0 && match.playerBGames === 0) {
        errors.push(
          `Match ${index + 1}: Match must have at least one completed game`
        );
      }

      // Check match format validity
      const totalGames = match.playerAGames + match.playerBGames;
      const requiredWins = Math.ceil(match.bestOf / 2);

      if (
        match.playerAGames >= requiredWins ||
        match.playerBGames >= requiredWins
      ) {
        // Match is complete, check if total games makes sense
        if (totalGames > match.bestOf) {
          errors.push(
            `Match ${index + 1}: Too many games played for best of ${
              match.bestOf
            } format`
          );
        }
      }

      // Validate that games won matches actual completed games
      const completedGames = match.games.filter(
        (game) => game.winner !== null
      ).length;
      if (totalGames !== completedGames) {
        errors.push(
          `Match ${
            index + 1
          }: Games won (${totalGames}) doesn't match completed games (${completedGames})`
        );
      }

      // Check if match should have a winner
      if (
        match.playerAGames >= requiredWins ||
        match.playerBGames >= requiredWins
      ) {
        if (!match.winner) {
          errors.push(
            `Match ${index + 1}: Match is complete but no winner declared`
          );
        }
      }

      // Validate individual games
      match.games.forEach((game, gameIndex) => {
        if (game.playerAPoints === 0 && game.playerBPoints === 0) {
          errors.push(
            `Match ${index + 1}, Game ${
              gameIndex + 1
            }: Game has no score recorded`
          );
        }

        // Check for unrealistic scores
        if (game.playerAPoints > 30 || game.playerBPoints > 30) {
          errors.push(
            `Match ${index + 1}, Game ${
              gameIndex + 1
            }: Score seems unreasonably high (max expected ~30)`
          );
        }

        // Validate game completion logic
        const maxScore = Math.max(game.playerAPoints, game.playerBPoints);
        const minScore = Math.min(game.playerAPoints, game.playerBPoints);
        const diff = maxScore - minScore;

        if (game.winner && maxScore < match.pointsToWin) {
          errors.push(
            `Match ${index + 1}, Game ${
              gameIndex + 1
            }: Winner declared but neither player reached ${
              match.pointsToWin
            } points`
          );
        }

        if (
          game.winner &&
          maxScore >= match.pointsToWin &&
          diff < 2 &&
          maxScore < match.pointsToWin + 10
        ) {
          errors.push(
            `Match ${index + 1}, Game ${
              gameIndex + 1
            }: Game should continue until 2-point lead or max ${
              match.pointsToWin + 10
            } points`
          );
        }
      });
    });

    return errors;
  };

  const handleSaveAll = async () => {
    const validationErrors = validateMatches();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: squashMatches,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all squash matches!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  const handleAddSquashMatch = () => {
    const newMatch = {
      matchType: "recreational", // recreational, competitive, tournament
      bestOf: 3, // best of 3 or 5 games
      pointsToWin: 11, // points needed to win a game (11 or 15)
      playerA: null,
      playerAId: null,
      playerB: null,
      playerBId: null,
      games: [], // array of game objects
      playerAGames: 0, // games won by player A
      playerBGames: 0, // games won by player B
      winner: null, // playerA, playerB, or null
      duration: 0, // match duration in minutes
      courtNumber: 1,
    };
    setSquashMatches((prev) => [...prev, newMatch]);
  };

  const handleRemoveMatch = (index) => {
    setSquashMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setSquashMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />

      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            {data.sport} Matches at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed squash match information"
            )}
          </p>
        </div>

        <button
          onClick={handleAddSquashMatch}
          className={classes.addButton}
        >
          Add New Squash Match
        </button>

        {squashMatches.length === 0 && (
          <div className={classes.noMatchesMessage}>
            No matches added yet. Click the button above to add your first
            squash match.
          </div>
        )}

        {squashMatches.map((match, index) => (
          <SquashMatchForm
            key={index}
            match={match}
            matchNumber={index + 1}
            availableUsers={availableUsers}
            onUpdate={(updatedMatch) => handleUpdateMatch(index, updatedMatch)}
            onRemove={() => handleRemoveMatch(index)}
          />
        ))}

        {squashMatches.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Matches
          </button>
        )}
      </div>
    </>
  );
};

const SquashMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    formGroup: "space-y-4",
    label: "font-medium text-gray-700",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    select:
      "px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    teamSection: "bg-white p-5 rounded-lg shadow-sm mb-5",
    playerCard:
      "bg-gray-50 p-3 rounded-lg mb-3 flex justify-between items-center",
    deleteButton: "bg-red-500 text-white px-2 py-1 rounded text-xs",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
    scoreCard:
      "flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3",
    scoreBox:
      "text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto",
    scoreLabel: "text-sm text-gray-500 mb-1",
    scoreValue: "text-3xl font-bold text-gray-800",
    winnerBox: "text-center p-4 rounded-lg bg-blue-50 min-w-[120px]",
  };

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const addGame = () => {
    const updated = { ...localMatch };
    updated.games.push({
      gameNumber: updated.games.length + 1,
      playerAPoints: 0,
      playerBPoints: 0,
      winner: null,
      duration: 0, // game duration in minutes
    });
    updateParent(updated);
  };

  const updateGame = (gameIndex, field, value) => {
    const updated = { ...localMatch };
    updated.games[gameIndex][field] = value;

    // Auto-determine game winner
    const game = updated.games[gameIndex];
    const targetPoints = updated.pointsToWin;

    if (
      game.playerAPoints >= targetPoints ||
      game.playerBPoints >= targetPoints
    ) {
      const diff = Math.abs(game.playerAPoints - game.playerBPoints);

      // Check if someone has won with proper margin
      if (
        game.playerAPoints >= targetPoints &&
        game.playerAPoints >= game.playerBPoints + 2
      ) {
        game.winner = "playerA";
      } else if (
        game.playerBPoints >= targetPoints &&
        game.playerBPoints >= game.playerAPoints + 2
      ) {
        game.winner = "playerB";
      } else if (
        Math.max(game.playerAPoints, game.playerBPoints) >=
        targetPoints + 10
      ) {
        // Cap at target + 10 and award to leader
        game.winner =
          game.playerAPoints > game.playerBPoints ? "playerA" : "playerB";
      } else {
        game.winner = null; // Still need to win by 2
      }
    } else {
      game.winner = null;
    }

    recalculateMatchResults(updated);
  };

  const removeGame = (gameIndex) => {
    const updated = { ...localMatch };
    updated.games.splice(gameIndex, 1);

    // Renumber games
    updated.games.forEach((game, index) => {
      game.gameNumber = index + 1;
    });

    recalculateMatchResults(updated);
  };

  const recalculateMatchResults = (updated) => {
    let playerAGames = 0;
    let playerBGames = 0;

    updated.games.forEach((game) => {
      if (game.winner === "playerA") playerAGames++;
      else if (game.winner === "playerB") playerBGames++;
    });

    updated.playerAGames = playerAGames;
    updated.playerBGames = playerBGames;

    // Determine match winner
    const requiredWins = Math.ceil(updated.bestOf / 2);
    if (playerAGames >= requiredWins) {
      updated.winner = "playerA";
    } else if (playerBGames >= requiredWins) {
      updated.winner = "playerB";
    } else {
      updated.winner = null;
    }

    updateParent(updated);
  };

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Squash Match #{matchNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Match
        </button>
      </div>

      {/* Squash Match Guidance */}
      <div className={formClasses.guidanceBox}>
        <h4 className={formClasses.guidanceTitle}>
          🎾 Squash Match Guide
        </h4>
        <div className={formClasses.guidanceContent}>
          <p>
            <strong>Format:</strong> Individual sport (1 vs 1)
          </p>
          <p>
            <strong>Scoring:</strong> Rally scoring - point awarded on every
            rally
          </p>
          <p>
            <strong>Games:</strong> First to 11 points (or 15), must win by 2
            points
          </p>
          <p>
            <strong>Match:</strong> Best of 3 games (first to win 2) or Best of
            5 games (first to win 3)
          </p>
          <p>
            <strong>Overtime:</strong> If tied at 10-10 (or 14-14), play
            continues until 2-point lead
          </p>
          <p>
            <strong>Court:</strong> Enclosed court with front wall, side walls,
            and back wall
          </p>
          <p>
            <strong>Tip:</strong> Winners auto-determined by scoring rules. Add
            games one by one!
          </p>
        </div>
      </div>

      {/* Match Setup */}
      <div className={formClasses.formGroup}>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Player A</label>
            <PlayerSlot
              availableUsers={availableUsers}
              selectedPlayerId={localMatch.playerA}
              selectedPlayerName={
                localMatch.playerAName ||
                (localMatch.playerA
                  ? availableUsers.find(
                      (u) => (u.id || u.user_id) == localMatch.playerA
                    )?.full_name || "Player A"
                  : "Player A")
              }
              excludedPlayerIds={[localMatch.playerB].filter(
                (id) => id && !String(id).startsWith("custom_")
              )}
              onSelectPlayer={(userInfo, customName) => {
                const playerId =
                  userInfo?.id ||
                  userInfo?.user_id ||
                  (customName ? `custom_${Date.now()}` : null);
                const playerName =
                  userInfo?.full_name || customName || "Guest Player";
                updateParent({
                  ...localMatch,
                  playerA: playerId,
                  playerAId: playerId,
                  playerAName: playerName,
                });
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Player B</label>
            <PlayerSlot
              availableUsers={availableUsers}
              selectedPlayerId={localMatch.playerB}
              selectedPlayerName={
                localMatch.playerBName ||
                (localMatch.playerB
                  ? availableUsers.find(
                      (u) => (u.id || u.user_id) == localMatch.playerB
                    )?.full_name || "Player B"
                  : "Player B")
              }
              excludedPlayerIds={[localMatch.playerA].filter(
                (id) => id && !String(id).startsWith("custom_")
              )}
              onSelectPlayer={(userInfo, customName) => {
                const playerId =
                  userInfo?.id ||
                  userInfo?.user_id ||
                  (customName ? `custom_${Date.now()}` : null);
                const playerName =
                  userInfo?.full_name || customName || "Guest Player";
                updateParent({
                  ...localMatch,
                  playerB: playerId,
                  playerBId: playerId,
                  playerBName: playerName,
                });
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Match Format</label>
            <select
              value={localMatch.bestOf}
              onChange={(e) =>
                updateParent({ ...localMatch, bestOf: Number(e.target.value) })
              }
              className={`${formClasses.select} w-full`}
            >
              <option value={3}>Best of 3</option>
              <option value={5}>Best of 5</option>
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Points to Win</label>
            <select
              value={localMatch.pointsToWin}
              onChange={(e) =>
                updateParent({
                  ...localMatch,
                  pointsToWin: Number(e.target.value),
                })
              }
              className={`${formClasses.select} w-full`}
            >
              <option value={11}>11 Points</option>
              <option value={15}>15 Points</option>
            </select>
          </div>
        </div>
      </div>

      {/* Game Management */}
      <div className={formClasses.teamSection}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Games</h4>
          <button
            onClick={addGame}
            className={formClasses.addButton}
          >
            Add Game
          </button>
        </div>

        {localMatch.games.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            No games added yet. Click "Add Game" to start.
          </p>
        ) : (
          <div className="space-y-4">
            {localMatch.games.map((game, gameIndex) => (
              <div key={gameIndex} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h5 className="font-medium text-gray-700">
                    Game {game.gameNumber}
                  </h5>
                  <button
                    onClick={() => removeGame(gameIndex)}
                    className="text-red-500 hover:text-red-600 font-medium px-3 py-1 rounded transition-colors duration-200"
                  >
                    Remove
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      {localMatch.playerAName || "Player A"}
                    </p>
                    <input
                      type="number"
                      value={game.playerAPoints}
                      onChange={(e) =>
                        updateGame(
                          gameIndex,
                          "playerAPoints",
                          Math.max(0, Number(e.target.value))
                        )
                      }
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="30"
                    />
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">VS</p>
                    <div className="text-2xl font-bold text-gray-400">-</div>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      {localMatch.playerBName || "Player B"}
                    </p>
                    <input
                      type="number"
                      value={game.playerBPoints}
                      onChange={(e) =>
                        updateGame(
                          gameIndex,
                          "playerBPoints",
                          Math.max(0, Number(e.target.value))
                        )
                      }
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="30"
                    />
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Winner</p>
                    <div className="text-sm font-medium">
                      {game.winner === "playerA" ? (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {localMatch.playerAName || "Player A"}
                        </span>
                      ) : game.winner === "playerB" ? (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {localMatch.playerBName || "Player B"}
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                          In Progress
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Duration (min)</p>
                    <input
                      type="number"
                      value={game.duration}
                      onChange={(e) =>
                        updateGame(
                          gameIndex,
                          "duration",
                          Math.max(0, Number(e.target.value))
                        )
                      }
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="180"
                    />
                  </div>
                </div>

                {/* Game Status */}
                <div className="mt-3 text-center">
                  {game.playerAPoints >= localMatch.pointsToWin - 1 &&
                    game.playerBPoints >= localMatch.pointsToWin - 1 &&
                    !game.winner && (
                      <p className="text-orange-600 text-sm font-medium">
                        ⚠️ Must win by 2 points! Current difference:{" "}
                        {Math.abs(game.playerAPoints - game.playerBPoints)}
                      </p>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Match Summary */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-4">
          Match Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {localMatch.playerAName || "Player A"}
              </p>
              <p className="text-3xl font-bold text-blue-600">
                {localMatch.playerAGames}
              </p>
              <p className="text-sm text-gray-500">games won</p>
            </div>
          </div>

          <div className="text-center">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Match Status</p>
              <p className="text-lg font-bold text-yellow-600">
                {localMatch.winner === "playerA"
                  ? "Player A Wins"
                  : localMatch.winner === "playerB"
                  ? "Player B Wins"
                  : "In Progress"}
              </p>
              <p className="text-sm text-gray-500">
                Best of {localMatch.bestOf} • {localMatch.pointsToWin} points
              </p>
            </div>
          </div>

          <div className="text-center">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {localMatch.playerBName || "Player B"}
              </p>
              <p className="text-3xl font-bold text-green-600">
                {localMatch.playerBGames}
              </p>
              <p className="text-sm text-gray-500">games won</p>
            </div>
          </div>
        </div>

        {/* Additional Match Info */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={formClasses.label}>
              Match Type
            </label>
            <select
              value={localMatch.matchType}
              onChange={(e) =>
                updateParent({ ...localMatch, matchType: e.target.value })
              }
              className={formClasses.input}
            >
              <option value="recreational">Recreational</option>
              <option value="competitive">Competitive</option>
              <option value="tournament">Tournament</option>
            </select>
          </div>

          <div>
            <label className={formClasses.label}>
              Court Number
            </label>
            <input
              type="number"
              value={localMatch.courtNumber}
              onChange={(e) =>
                updateParent({
                  ...localMatch,
                  courtNumber: Math.max(1, Number(e.target.value)),
                })
              }
              className={formClasses.input}
              min="1"
              max="20"
            />
          </div>

          <div>
            <label className={formClasses.label}>
              Total Duration (min)
            </label>
            <input
              type="number"
              value={localMatch.duration}
              onChange={(e) =>
                updateParent({
                  ...localMatch,
                  duration: Math.max(0, Number(e.target.value)),
                })
              }
              className={formClasses.input}
              min="0"
              max="300"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// PlayerSlot component for single player selection per slot
const PlayerSlot = ({
  availableUsers,
  selectedPlayerId,
  selectedPlayerName,
  onSelectPlayer,
  excludedPlayerIds = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const isSlotEmpty =
    !selectedPlayerId ||
    !selectedPlayerName ||
    selectedPlayerName === "Player A" ||
    selectedPlayerName === "Player B";

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSave = () => {
    if (useCustomName && customName.trim()) {
      onSelectPlayer(null, customName.trim());
      setCustomName("");
      setIsEditing(false);
    } else if (!useCustomName && selectedUser) {
      onSelectPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setCustomName("");
    setUseCustomName(false);
    setIsEditing(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Show editing UI if editing or no player selected
  if (isEditing || isSlotEmpty) {
    return (
      <div className="flex flex-col gap-2">
        {!useCustomName ? (
          <>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="Search for system users..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-[200px] overflow-y-auto z-[1000] shadow-lg">
                  {suggestions.map((user) => {
                    const isAlreadySelected =
                      excludedPlayerIds.includes(user.id) ||
                      excludedPlayerIds.includes(user.user_id);
                    return (
                      <div
                        key={user.id || user.user_id}
                        className={`px-3 py-2 border-b border-gray-100 text-sm transition-colors ${
                          isAlreadySelected
                            ? "opacity-50 cursor-not-allowed bg-gray-100"
                            : "cursor-pointer hover:bg-gray-50"
                        }`}
                        onClick={() =>
                          !isAlreadySelected && handleSelectUser(user)
                        }
                      >
                        <div className="font-medium">
                          {user.full_name || user.username}
                          {isAlreadySelected && (
                            <span className="text-red-600 ml-2">
                              (Already Added)
                            </span>
                          )}
                        </div>
                        {user.email && (
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {showSuggestions &&
                searchTerm.length >= 2 &&
                suggestions.length === 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg shadow-lg">
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      No users found
                    </div>
                  </div>
                )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={!selectedUser}
                className={`px-3 py-1.5 rounded text-xs font-medium ${
                  selectedUser
                    ? "bg-blue-500 hover:bg-blue-600 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs font-medium"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Enter custom player name"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={!customName.trim()}
                className={`px-3 py-1.5 rounded text-xs font-medium ${
                  customName.trim()
                    ? "bg-blue-500 hover:bg-blue-600 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs font-medium"
              >
                Cancel
              </button>
            </div>
          </>
        )}
        <span
          onClick={() => setUseCustomName(!useCustomName)}
          className="text-xs text-blue-500 cursor-pointer underline"
        >
          {useCustomName ? "Use system users" : "Add custom name"}
        </span>
      </div>
    );
  }

  // If player is selected and not editing, show the selected player
  return (
    <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
      <span className="text-sm text-gray-800">{selectedPlayerName}</span>
      <button
        onClick={() => setIsEditing(true)}
        className="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-xs font-medium"
      >
        Edit
      </button>
    </div>
  );
};

export default AddSquash;
